/**
 * URL search-param schemas.
 *
 * Filters live in the URL so views are linkable and bookmarkable (design §2.4).
 * That makes the URL an untrusted input: someone will paste a truncated link, or
 * a saved bookmark will outlive a filter we removed. Every field therefore has a
 * `.catch(...)`, so a malformed param degrades to the default instead of
 * throwing inside the router's `validateSearch` and blanking the route.
 *
 * The set is deliberately SMALL: it is exactly the filters `apps/api` honours
 * today. `q`, `tag`, `hasFollowUp`, `enrichmentStatus`, `metAtEventId` and
 * `lastInteractionBefore` are contract-declared but 422 at the server, so they
 * are not in the URL — a bookmarked `?q=priya` that silently does nothing is
 * worse than no search box at all.
 */

import { accessSourceFilterSchema, contactTypeSchema } from '@crm/contracts';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { z } from 'zod';

export const contactsSearchSchema = z.object({
  /** ONE type. `apps/api` raises `contacts.list.type with more than one value`. */
  type: contactTypeSchema.optional().catch(undefined),
  /** ONE list, same reason. */
  listId: z.uuid().optional().catch(undefined),
  source: accessSourceFilterSchema.catch('all').default('all'),
});

export type ContactsSearch = z.output<typeof contactsSearchSchema>;

/**
 * `validateSearch` for `/contacts`.
 *
 * The `& SearchSchemaInput` marker is load-bearing, not decoration. Without it
 * TanStack Router takes the validator's OUTPUT as the type a `<Link to="/contacts">`
 * must supply — and since `source` has a default it is required on the output,
 * so every link to the list would be forced to spell out `search={{source:'all'}}`.
 * The marker tells the router "the input is looser than the output", which is the
 * whole point of having defaults.
 */
export function parseContactsSearch(
  input: Record<string, unknown> & SearchSchemaInput,
): ContactsSearch {
  return contactsSearchSchema.parse(input);
}

/**
 * `validateSearch` for `/login`; `redirect` carries the attempted href.
 *
 * Only an in-app path is accepted. The value round-trips through a URL anyone
 * can craft, so `https://evil.example` and the protocol-relative `//evil.example`
 * are dropped here rather than trusted to be inert downstream — the guard writes
 * `location.href`, which always starts with a single `/`.
 */
export function parseLoginSearch(input: Record<string, unknown> & SearchSchemaInput): {
  redirect: string | undefined;
} {
  const raw = input.redirect;
  const isInAppPath = typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//');
  return { redirect: isInAppPath ? raw : undefined };
}

/**
 * `validateSearch` for `/join`; `token` is the invitation itself.
 *
 * Deliberately unvalidated beyond "a non-empty string". The token's shape is the
 * server's business, and a client-side format check would only ever produce a
 * SECOND way for a link to look broken — one that never reaches the endpoint
 * that can actually answer. An absent or empty token renders the same
 * "this link does not work" as one the server rejects.
 */
export function parseJoinSearch(input: Record<string, unknown> & SearchSchemaInput): {
  token: string | undefined;
} {
  const raw = input.token;
  return { token: typeof raw === 'string' && raw !== '' ? raw : undefined };
}

/**
 * Applies a filter change, dropping cleared keys.
 *
 * `undefined` removes the param from the URL rather than serialising as
 * `?type=undefined` — the difference between a clean shareable link and one that
 * fails to parse on the other end.
 */
export function applyContactFilter(
  current: ContactsSearch,
  patch: Partial<ContactsSearch>,
): ContactsSearch {
  const next: ContactsSearch = { ...current, ...patch };
  return {
    source: next.source,
    ...(next.type === undefined ? {} : { type: next.type }),
    ...(next.listId === undefined ? {} : { listId: next.listId }),
  };
}

/**
 * `validateSearch` for `/contacts/:id`.
 *
 * `focus` carries an interaction id from a digest citation (design §2.7's
 * `WhyThisCard`: "links straight to the transcript span"). It is a URL param
 * rather than component state for the reason every other filter is: the link has
 * to be pasteable, and a citation someone forwards has to land on the same row.
 *
 * `.catch(undefined)` matters more here than elsewhere — the id is
 * server-generated and arrives through a URL anyone can edit, so a malformed one
 * must degrade to "no focus" rather than throw inside `validateSearch` and blank
 * the whole contact page over a decoration.
 */
export const contactDetailSearchSchema = z.object({
  focus: z.uuid().optional().catch(undefined),
});

export type ContactDetailSearch = z.output<typeof contactDetailSearchSchema>;

export function parseContactDetailSearch(
  input: Record<string, unknown> & SearchSchemaInput,
): ContactDetailSearch {
  return contactDetailSearchSchema.parse(input);
}

/**
 * `/lists?q=` — server-side, unlike the contact list.
 *
 * `lists.list` honours `q` (it is `contacts.list.q` that 422s), so this one is a
 * real filter rather than a param the server ignores.
 */
export const listsSearchSchema = z.object({
  q: z.string().min(1).max(200).optional().catch(undefined),
});

export type ListsSearch = z.output<typeof listsSearchSchema>;

export function parseListsSearch(input: Record<string, unknown> & SearchSchemaInput): ListsSearch {
  return listsSearchSchema.parse(input);
}

/**
 * `/events?q=&kind=&from=&to=` — every one of these is honoured server-side.
 *
 * The events page shipped with a placard that ADMITTED as much ("events.list
 * honours q / kind / from / to server-side; the controls are not built"). The
 * controls are built now and this is their URL shape.
 *
 * `from`/`to` are stored as the `YYYY-MM-DD` a date input produces, NOT as
 * instants: a bookmarked range must mean the same days when it is reopened in
 * another timezone, and the widening to an instant happens once, in the request
 * builder, against the reader's own clock.
 */
const YMD = /^\d{4}-\d{2}-\d{2}$/;

export const eventsSearchSchema = z.object({
  q: z.string().min(1).max(200).optional().catch(undefined),
  kind: z
    .enum(['conference', 'dinner', 'demo_day', 'meeting', 'other'])
    .optional()
    .catch(undefined),
  from: z.string().regex(YMD).optional().catch(undefined),
  to: z.string().regex(YMD).optional().catch(undefined),
});

export type EventsSearch = z.output<typeof eventsSearchSchema>;

export function parseEventsSearch(
  input: Record<string, unknown> & SearchSchemaInput,
): EventsSearch {
  return eventsSearchSchema.parse(input);
}

/** Drops cleared keys so a cleared filter leaves no `?kind=undefined` behind. */
export function applyEventFilter(
  current: EventsSearch,
  patch: Partial<EventsSearch>,
): EventsSearch {
  const next: EventsSearch = { ...current, ...patch };
  return {
    ...(next.q === undefined || next.q === '' ? {} : { q: next.q }),
    ...(next.kind === undefined ? {} : { kind: next.kind }),
    ...(next.from === undefined || next.from === '' ? {} : { from: next.from }),
    ...(next.to === undefined || next.to === '' ? {} : { to: next.to }),
  };
}

/**
 * `/hermes?session=` — which conversation is open.
 *
 * In the URL rather than in component state so a conversation is linkable and
 * survives a reload, and so the mobile layout can treat "no session" as "show
 * the session list" without a second piece of state to keep in sync.
 */
export const hermesSearchSchema = z.object({
  session: z.uuid().optional().catch(undefined),
});

export type HermesSearch = z.output<typeof hermesSearchSchema>;

export function parseHermesSearch(
  input: Record<string, unknown> & SearchSchemaInput,
): HermesSearch {
  return hermesSearchSchema.parse(input);
}
